import { useEffect, useState } from 'react';
import { Loader2, Shield, Zap } from 'lucide-react';

interface SplashScreenProps {
  onComplete?: () => void;
  duration?: number;
}

export const SplashScreen = ({ onComplete, duration = 2500 }: SplashScreenProps) => {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    { label: 'Sistem başlatılıyor...', icon: Shield },
    { label: 'Bağlantı kuruluyor...', icon: Zap },
    { label: 'Hazırlanıyor...', icon: Loader2 }
  ];

  useEffect(() => {
    const stepDuration = duration / steps.length;
    
    const interval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + (100 / (duration / 50));
        
        // Update current step based on progress
        const stepIndex = Math.floor((newProgress / 100) * steps.length);
        setCurrentStep(Math.min(stepIndex, steps.length - 1));
        
        if (newProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            onComplete?.();
          }, 200);
          return 100;
        }
        
        return newProgress;
      });
    }, 50);

    return () => clearInterval(interval);
  }, [duration, onComplete, steps.length]);

  const CurrentIcon = steps[currentStep]?.icon || Loader2;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/20 via-transparent to-accent/10"></div>
        <div className="absolute top-20 left-20 w-32 h-32 rounded-full bg-accent/10 blur-3xl animate-pulse"></div>
        <div className="absolute bottom-32 right-16 w-48 h-48 rounded-full bg-accent/5 blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 text-center space-y-8 px-8">
        {/* Logo Area */}
        <div className="space-y-6">
          <div className="relative">
            {/* Logo Background Glow */}
            <div className="absolute inset-0 bg-accent/20 blur-2xl rounded-full animate-pulse"></div>
            
            {/* Logo Container */}
            <div className="relative w-24 h-24 mx-auto rounded-2xl bg-gradient-to-br from-accent to-accent/80 shadow-2xl flex items-center justify-center">
              <Shield className="w-12 h-12 text-white" />
            </div>
          </div>

          {/* App Title */}
          <div className="space-y-2">
            <h1 className="text-h1 font-bold text-foreground">
              MüşteriX
            </h1>
            <p className="text-caption text-muted-foreground">
              Mağaza Yönetimi ve Müşteri Takibi
            </p>
          </div>
        </div>

        {/* Loading Section */}
        <div className="space-y-6">
          {/* Current Step */}
          <div className="flex items-center justify-center gap-3">
            <div className="relative">
              <CurrentIcon 
                className={`w-6 h-6 text-accent ${
                  currentStep === 2 ? 'animate-spin' : 'animate-pulse'
                }`} 
              />
            </div>
            <span className="text-body text-muted-foreground font-medium">
              {steps[currentStep]?.label}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="w-64 mx-auto">
            <div className="glass-panel p-1">
              <div className="relative h-2 bg-muted/20 rounded-full overflow-hidden">
                <div 
                  className="absolute left-0 top-0 h-full bg-gradient-to-r from-accent to-accent/80 rounded-full transition-all duration-200 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>

          {/* Progress Percentage */}
          <div className="text-caption text-muted-foreground font-tabular">
            {Math.round(progress)}%
          </div>
        </div>

        {/* Version Info */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <p className="text-micro text-muted-foreground">
            
          </p>
        </div>
      </div>
    </div>
  );
};
